<div class="field-grid path-builder">
  <div class="path-header">
    <div class="path-output">
      <span class="path-output-label">Built path</span>
      <input
        class="path-preview primary"
        [class.invalid]="api.isBuiltPathInvalid(mappingIndex, fieldIndex)"
        type="text"
        [value]="field.path"
        readonly
        placeholder="Select fields to build path"
      />
      @if (api.isBuiltPathInvalid(mappingIndex, fieldIndex)) {
        <span class="path-invalid-hint">
          Saved path is no longer valid for current Salesforce metadata.
        </span>
      }
    </div>
    <div class="datatype-inline">
      <label class="datatype-label">Data type (auto)</label>
      <input class="datatype-readonly" type="text" [value]="field.dataType || 'text'" readonly />
    </div>
  </div>

  <label class="builder-label">Path builder</label>
  <div class="path-builder-shell">
    @if (api.getSelectedPathSegments(mappingIndex, fieldIndex).length) {
      <div class="selected-segments">
        @for (segment of api.getSelectedPathSegments(mappingIndex, fieldIndex); track segment) {
          <span class="segment-chip">
            {{ segment }}
          </span>
        }
      </div>
    } @else {
      <span class="muted">No path selected yet.</span>
    }

    <div class="path-levels">
      @for (level of api.getPathLevels(mappingIndex, fieldIndex); track level) {
        <div class="path-level-row">
          <label class="level-label">{{ api.getPathLevelLabel(mappingIndex, fieldIndex, level) }}</label>
          <div class="path-combobox" cdkOverlayOrigin #pathOrigin="cdkOverlayOrigin">
            <input
              type="text"
              [ngModel]="api.getPathLevelSearchTerm(mappingIndex, fieldIndex, level) || api.getSelectedPathLevelLabel(mappingIndex, fieldIndex, level)"
              (ngModelChange)="api.onPathLevelSearchChange(mappingIndex, fieldIndex, level, $event)"
              (focus)="api.setPathLevelOpen(mappingIndex, fieldIndex, level, true)"
              (keydown.escape)="api.closePathLevel(mappingIndex, fieldIndex, level)"
              [placeholder]="level === 0 ? 'Search first field...' : 'Search next related field...'"
            />
            <ng-template
              cdkConnectedOverlay
              [cdkConnectedOverlayOrigin]="pathOrigin"
              [cdkConnectedOverlayOpen]="api.isPathLevelOpen(mappingIndex, fieldIndex, level)"
              [cdkConnectedOverlayHasBackdrop]="true"
              [cdkConnectedOverlayBackdropClass]="'cdk-overlay-transparent-backdrop'"
              [cdkConnectedOverlayPanelClass]="'sf-path-overlay-panel'"
              [cdkConnectedOverlayPositions]="pathDropdownPositions"
              [cdkConnectedOverlayFlexibleDimensions]="true"
              [cdkConnectedOverlayPush]="true"
              [cdkConnectedOverlayViewportMargin]="16"
              (backdropClick)="api.closePathLevel(mappingIndex, fieldIndex, level)"
              (detach)="api.closePathLevel(mappingIndex, fieldIndex, level)"
            >
              <div
                class="path-dropdown sf-path-dropdown"
                [attr.data-path-level-key]="api.getPathLevelDomKey(mappingIndex, fieldIndex, level)"
                [style.width.px]="api.getFlyoutDropdownWidth(mappingIndex, fieldIndex, level)"
                (scroll)="api.onFlyoutScroll(mappingIndex, fieldIndex, level)"
                (mousedown)="$event.stopPropagation()"
              >
                <div class="flyout-toolbar">
                  @if (api.getFlyoutBreadcrumb(mappingIndex, fieldIndex, level)) {
                    <div class="flyout-breadcrumb">
                      {{ api.getFlyoutBreadcrumb(mappingIndex, fieldIndex, level) }}
                    </div>
                  }
                  @if (api.canFlyoutScrollLeft(mappingIndex, fieldIndex, level) || api.canFlyoutScrollRight(mappingIndex, fieldIndex, level)) {
                    <div class="flyout-nav">
                      <button
                        type="button"
                        class="flyout-nav-btn"
                        [disabled]="!api.canFlyoutScrollLeft(mappingIndex, fieldIndex, level)"
                        (mousedown)="api.scrollFlyoutColumns(mappingIndex, fieldIndex, level, 'left'); $event.preventDefault()"
                      >
                        <span>&lt;</span>
                      </button>
                      <button
                        type="button"
                        class="flyout-nav-btn"
                        [disabled]="!api.canFlyoutScrollRight(mappingIndex, fieldIndex, level)"
                        (mousedown)="api.scrollFlyoutColumns(mappingIndex, fieldIndex, level, 'right'); $event.preventDefault()"
                      >
                        <span>&gt;</span>
                      </button>
                    </div>
                  }
                </div>
                <div class="flyout-columns">
                  @for (
                    column of api.getFlyoutColumns(mappingIndex, fieldIndex, level);
                    track api.trackByFlyoutColumn($index, column);
                    let columnIndex = $index
                  ) {
                    <div class="flyout-column">
                      <input
                        type="text"
                        [ngModel]="column.searchTerm"
                        (ngModelChange)="api.onFlyoutColumnSearchChange(mappingIndex, fieldIndex, level, columnIndex, $event)"
                        [placeholder]="columnIndex === 0 ? 'Search fields...' : 'Search related fields...'"
                      />
                      @if (column.loading) {
                        <div class="path-status muted">Loading fields...</div>
                      }
                      @if (column.error) {
                        <div class="path-status error">{{ column.error }}</div>
                      }
                      @for (
                        option of api.getFilteredFlyoutColumnOptions(mappingIndex, fieldIndex, level, columnIndex);
                        track api.trackByPathOption($index, option)
                      ) {
                        <button
                          type="button"
                          class="path-option"
                          [class.relation-selected]="api.isRelationOptionSelectedForColumn(mappingIndex, fieldIndex, level, columnIndex, option)"
                          (mouseenter)="api.onFlyoutOptionHover(mappingIndex, fieldIndex, level, columnIndex, option)"
                          (mousedown)="api.onFlyoutOptionSelect(mappingIndex, fieldIndex, level, columnIndex, option); $event.preventDefault()"
                        >
                          <span>{{ api.formatPathOption(option) }}</span>
                          @if (option.type === 'to-one') {
                            <span class="relation-arrow">></span>
                          }
                        </button>
                      }
                      @if (!column.loading && !column.error && !api.hasSelectableFlyoutOptions(mappingIndex, fieldIndex, level, columnIndex)) {
                        <div class="path-option-empty">
                          No matching fields
                        </div>
                      }
                      @if (api.isAtAssociatedEntityDepthCap(mappingIndex, fieldIndex, level, columnIndex) && api.hasSelectableFlyoutOptions(mappingIndex, fieldIndex, level, columnIndex)) {
                        <div class="path-option-empty">
                          Max related depth reached ({{ maxAssociatedEntityDepth }})
                        </div>
                      }
                    </div>
                  }
                </div>
              </div>
            </ng-template>
          </div>
          @if (api.getPathLevelLoading(mappingIndex, fieldIndex, level)) {
            <span class="path-status muted">Loading fields...</span>
          }
          @if (api.getPathLevelError(mappingIndex, fieldIndex, level)) {
            <span class="path-status error">
              {{ api.getPathLevelError(mappingIndex, fieldIndex, level) }}
            </span>
          }
        </div>
      }
    </div>

    <div class="path-buttons">
      <button type="button" class="inline-secondary-btn" (click)="api.stepBackPath(mappingIndex, fieldIndex)" [disabled]="!api.canStepBackPath(mappingIndex, fieldIndex)">
        Back
      </button>
      <button class="clear-path-btn" type="button" (click)="api.clearPathPicker(mappingIndex, fieldIndex)">Clear</button>
    </div>
  </div>
</div>
